"use client";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";

interface ChartDataPoint {
  throttle: number;
  rpm: number;
  wattage: number;
  speed: number;
  rangeNm: number;
}

interface PerformanceChartsProps {
  data: ChartDataPoint[];
  currentThrottle: number;
}

function formatWattage(w: number): string {
  return w >= 1000 ? `${(w / 1000).toFixed(1)} kW` : `${w.toFixed(0)} W`;
}

export function PerformanceCharts({
  data,
  currentThrottle,
}: PerformanceChartsProps) {
  if (data.length === 0) return null;

  return (
    <div className="space-y-6">
      {/* Distance & Speed Chart */}
      <div>
        <h4 className="text-sm font-semibold text-foreground mb-2 text-center">
          Range &amp; Speed vs Throttle
        </h4>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
            <XAxis
              dataKey="throttle"
              tick={{ fontSize: 11 }}
              label={{ value: "Throttle %", position: "insideBottom", offset: -2, fontSize: 11 }}
            />
            <YAxis
              yAxisId="range"
              tick={{ fontSize: 11 }}
              label={{ value: "NM", angle: -90, position: "insideLeft", fontSize: 11 }}
            />
            <YAxis
              yAxisId="speed"
              orientation="right"
              tick={{ fontSize: 11 }}
              label={{ value: "knots", angle: 90, position: "insideRight", fontSize: 11 }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "var(--color-card)",
                border: "1px solid var(--color-border)",
                borderRadius: 8,
                fontSize: 12,
              }}
              formatter={(value, name) => {
                const v = Number(value);
                if (name === "rangeNm") return [`${v.toFixed(1)} NM`, "Range"];
                if (name === "speed") return [`${v.toFixed(1)} kts`, "Speed"];
                return [String(value), String(name)];
              }}
              labelFormatter={(v) => `${v}% throttle`}
            />
            <Legend
              formatter={(value) => {
                if (value === "rangeNm") return "Range (NM)";
                if (value === "speed") return "Speed (kts)";
                return value;
              }}
            />
            <ReferenceLine
              x={currentThrottle}
              yAxisId="range"
              stroke="var(--color-em-blue)"
              strokeDasharray="4 4"
              strokeWidth={2}
            />
            <Line
              yAxisId="range"
              type="monotone"
              dataKey="rangeNm"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={false}
            />
            <Line
              yAxisId="speed"
              type="monotone"
              dataKey="speed"
              stroke="#f97316"
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Power & RPM Chart */}
      <div>
        <h4 className="text-sm font-semibold text-foreground mb-2 text-center">
          Power &amp; RPM vs Throttle
        </h4>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
            <XAxis
              dataKey="throttle"
              tick={{ fontSize: 11 }}
              label={{ value: "Throttle %", position: "insideBottom", offset: -2, fontSize: 11 }}
            />
            <YAxis
              yAxisId="power"
              tick={{ fontSize: 11 }}
              tickFormatter={(v) => formatWattage(v)}
            />
            <YAxis
              yAxisId="rpm"
              orientation="right"
              tick={{ fontSize: 11 }}
              label={{ value: "RPM", angle: 90, position: "insideRight", fontSize: 11 }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "var(--color-card)",
                border: "1px solid var(--color-border)",
                borderRadius: 8,
                fontSize: 12,
              }}
              formatter={(value, name) => {
                const v = Number(value);
                if (name === "wattage") return [formatWattage(v), "Power"];
                if (name === "rpm") return [`${v.toFixed(0)}`, "RPM"];
                return [String(value), String(name)];
              }}
              labelFormatter={(v) => `${v}% throttle`}
            />
            <Legend
              formatter={(value) => {
                if (value === "wattage") return "Power";
                if (value === "rpm") return "RPM";
                return value;
              }}
            />
            <ReferenceLine
              x={currentThrottle}
              yAxisId="power"
              stroke="var(--color-em-blue)"
              strokeDasharray="4 4"
              strokeWidth={2}
            />
            <Line
              yAxisId="power"
              type="monotone"
              dataKey="wattage"
              stroke="#ef4444"
              strokeWidth={2}
              dot={false}
            />
            <Line
              yAxisId="rpm"
              type="monotone"
              dataKey="rpm"
              stroke="#22c55e"
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
